import { useState, useCallback, useEffect } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import { Search, Heart, Clock, Users, Loader2, Filter, X, ArrowRight, Check, ChevronLeft, ChevronRight } from 'lucide-react';
import axios from 'axios';
import { 
  trackDemoStepViewed, 
  trackDemoStepCompleted, 
  trackRecipeSearched, 
  trackRecipeViewed,
  trackSearchChipUsed 
} from '@/utils/analytics';
import MobileRecipeSearch from '@/components/MobileRecipeSearch';
import HealthLabelBadges from '@/components/HealthLabelBadges';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const RESULTS_PER_PAGE = 30;

const RecipeSearch = () => {
  const { api, isAuthenticated } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const isDemoMode = searchParams.get('demo') === 'true';
  
  // Mobile detection
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  // All hooks must be declared before any conditional returns
  const [query, setQuery] = useState('');
  const [diet, setDiet] = useState('');
  const [cuisine, setCuisine] = useState('');
  const [maxReadyTime, setMaxReadyTime] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [favorites, setFavorites] = useState(new Set());
  const [hasInteracted, setHasInteracted] = useState(false);
  const [selectedRecipe, setSelectedRecipe] = useState(null);
  const [showStep2Banner, setShowStep2Banner] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [totalResults, setTotalResults] = useState(0);
  const [lastSearchQuery, setLastSearchQuery] = useState('');
  
  const totalPages = Math.ceil(totalResults / RESULTS_PER_PAGE);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Track demo step 1 viewed
  useEffect(() => {
    if (isDemoMode) {
      trackDemoStepViewed(1, 'Find Recipes');
    }
  }, [isDemoMode]);

  // Search chips with display label and optimized search query
  // TheMealDB works best with simple single-word searches
  const suggestedSearches = [
    { label: 'Chicken pasta', emoji: '🍝', query: 'chicken' },
    { label: 'Healthy salads', emoji: '🥗', query: 'salad' },
    { label: 'Taco night', emoji: '🌮', query: 'taco beef' },
    { label: 'Quick & Easy', emoji: '⏱️', query: 'soup stir' },
    { label: 'Family favorites', emoji: '👨‍👩‍👧', query: 'pasta beef' },
  ];

  // Load user's favorites
  const loadFavorites = useCallback(async () => {
    if (!isAuthenticated) return;
    try {
      const response = await api.get('/favorites');
      const favIds = new Set(response.data.map(f => f.recipe_id));
      setFavorites(favIds);
    } catch (error) {
      console.error('Failed to load favorites:', error);
    }
  }, [api, isAuthenticated]);

  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]);

  // Render mobile version - must be after all hooks
  if (isMobile) {
    return <MobileRecipeSearch />;
  }

  const handleSearch = async (e) => {
    e.preventDefault();
    if (!query.trim()) {
      toast.error('Please enter a search term');
      return;
    }
    setCurrentPage(1);
    performSearch(query, 1);
  };

  const toggleFavorite = async (recipe) => {
    if (!isAuthenticated) {
      setHasInteracted(true);
      
      // In demo mode, show Step 2 banner instead of navigating away
      if (isDemoMode) {
        setShowStep2Banner(true);
        toast.info('Sign up to save this recipe!');
        return;
      }
      
      toast.info('Sign up to save this recipe!');
      setTimeout(() => navigate('/auth'), 1500);
      return;
    }
    
    const isFavorite = favorites.has(recipe.id);

    try {
      if (isFavorite) {
        await api.delete(`/favorites/${recipe.id}`);
        setFavorites(prev => {
          const next = new Set(prev);
          next.delete(recipe.id);
          return next;
        });
        toast.success('Removed from favorites');
      } else {
        await api.post('/favorites', {
          recipe_id: recipe.id,
          recipe_title: recipe.title,
          recipe_image: recipe.image,
          ready_in_minutes: recipe.readyInMinutes,
          servings: recipe.servings
        });
        setFavorites(prev => new Set([...prev, recipe.id]));
        toast.success('Added to favorites');
      }
    } catch (error) {
      toast.error('Failed to update favorites');
    }
  };

  const clearFilters = () => {
    setDiet('');
    setCuisine('');
    setMaxReadyTime(null);
  };

  const handleQuickSearch = (searchQuery, displayLabel) => {
    // Track search chip usage
    trackSearchChipUsed(displayLabel, isDemoMode);
    
    // Show the display label in the search box for user-friendliness
    setQuery(displayLabel || searchQuery);
    // Reset to page 1 for new search
    setCurrentPage(1);
    // But use the optimized query for the actual search
    performSearch(searchQuery, 1);
  };

  const performSearch = async (searchQuery, page = 1, filters = {}) => {
    if (!searchQuery.trim()) return;
    
    // Use overrides if provided, otherwise use state
    const effectiveMaxTime = filters.maxReadyTime !== undefined ? filters.maxReadyTime : maxReadyTime;
    const effectiveDiet = filters.diet !== undefined ? filters.diet : diet;
    const effectiveCuisine = filters.cuisine !== undefined ? filters.cuisine : cuisine;
    
    // Track recipe search
    trackRecipeSearched(searchQuery, isDemoMode);
    
    const offset = (page - 1) * RESULTS_PER_PAGE;
    setLoading(true);
    setSearched(true);
    setLastSearchQuery(searchQuery);

    try {
      const response = isAuthenticated 
        ? await api.post('/recipes/search', {
            query: searchQuery.trim(),
            number: RESULTS_PER_PAGE,
            offset: offset,
            diet: effectiveDiet || null,
            cuisine: effectiveCuisine || null,
            maxReadyTime: effectiveMaxTime || null
          })
        : await axios.post(`${API_URL}/api/recipes/search`, {
            query: searchQuery.trim(),
            number: RESULTS_PER_PAGE,
            offset: offset,
            diet: effectiveDiet || null,
            cuisine: effectiveCuisine || null,
            maxReadyTime: effectiveMaxTime || null
          });

      setRecipes(response.data.results || []);
      setTotalResults(response.data.totalResults || response.data.results?.length || 0);
      
      if (response.data.mock) {
        toast.info('Showing sample recipes. Add Spoonacular API key for full access.');
      }
      
      // Scroll to top of results on page change
      if (page > 1) {
        window.scrollTo({ top: 200, behavior: 'smooth' });
      }
    } catch (error) {
      console.error('Search failed:', error);
      toast.error('Failed to search recipes. Please try again.');
    } finally {
      setLoading(false);
    }
  };
  
  const goToPage = (page) => {
    if (page < 1 || page > totalPages || page === currentPage) return;
    setCurrentPage(page);
    performSearch(lastSearchQuery, page);
  };

  const handleRecipeClick = (e, recipe) => {
    if (isDemoMode && !isAuthenticated) {
      e.preventDefault(); // Prevent navigation in demo mode
      setHasInteracted(true);
      setSelectedRecipe(recipe.id);
      setShowStep2Banner(true);
      toast.success(`"${recipe.title}" selected!`, {
        description: "Ready to add to your meal plan",
        duration: 3000,
      });
    }
  };

  const diets = [
    'vegetarian', 'vegan', 'gluten free', 'ketogenic', 
    'paleo', 'whole30', 'pescetarian', 'dairy free'
  ];

  const cuisines = [
    'african', 'american', 'british', 'cajun', 'caribbean',
    'chinese', 'french', 'greek', 'indian', 'italian',
    'japanese', 'korean', 'mediterranean', 'mexican', 'thai'
  ];

  const cookTimeOptions = [
    { value: 15, label: 'Under 15 min', emoji: '⚡' },
    { value: 30, label: 'Under 30 min', emoji: '🍳' },
    { value: 45, label: 'Under 45 min', emoji: '👨‍🍳' },
    { value: 60, label: 'Under 1 hour', emoji: '🕐' },
  ];

  const quickDietOptions = [
    { value: 'vegetarian', label: 'Vegetarian', icon: '🥗' },
    { value: 'vegan', label: 'Vegan', icon: '🌱' },
    { value: 'gluten free', label: 'Gluten-Free', icon: '🌾' },
    { value: 'ketogenic', label: 'Keto', icon: '🥑' },
    { value: 'paleo', label: 'Paleo', icon: '🥩' },
  ];

  const quickCuisineOptions = [
    { value: 'italian', label: 'Italian', icon: '🇮🇹' },
    { value: 'mexican', label: 'Mexican', icon: '🇲🇽' },
    { value: 'chinese', label: 'Chinese', icon: '🇨🇳' },
    { value: 'indian', label: 'Indian', icon: '🇮🇳' },
    { value: 'mediterranean', label: 'Mediterranean', icon: '🫒' },
    { value: 'japanese', label: 'Japanese', icon: '🇯🇵' },
  ];

  return (
    <div className="min-h-screen py-8" data-testid="recipe-search-page">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Demo Mode Banners */}
        {isDemoMode && !isAuthenticated && !showStep2Banner && (
          <Card className="border-primary/50 bg-gradient-to-r from-primary/5 to-accent/5 mb-6" data-testid="demo-step1-banner">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-primary">Step 1</Badge>
                    <span className="font-semibold text-foreground">Find a recipe you love</span>
                  </div>
                  <span className="text-sm text-muted-foreground ml-[52px]">Search by dish, ingredient, or cuisine.</span>
                </div>
                {/* Contextual CTA - only shows after user interacts */}
                {hasInteracted && (
                  <Link to="/auth">
                    <Button size="sm" data-testid="demo-signup-cta">
                      Sign up to save this recipe
                      <ArrowRight className="h-4 w-4 ml-1" />
                    </Button>
                  </Link>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step 2 Banner - appears after clicking/favoriting a recipe */}
        {isDemoMode && !isAuthenticated && showStep2Banner && (
          <Card className="border-accent/50 bg-gradient-to-r from-accent/10 to-primary/5 mb-6" data-testid="demo-step2-banner">
            <CardContent className="py-4">
              <div className="flex items-center justify-between flex-wrap gap-4">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-3">
                    <Badge className="bg-accent text-accent-foreground">Step 2</Badge>
                    <span className="font-semibold text-foreground">Plan your week in minutes</span>
                  </div>
                  <span className="text-sm text-muted-foreground ml-[52px]">Drag recipes onto your calendar to build your meal plan.</span>
                </div>
                <Link to="/meal-planner?demo=true">
                  <Button size="sm" data-testid="demo-step2-goto-planner">
                    Go to Meal Planner
                    <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                </Link>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl md:text-4xl font-semibold font-['Playfair_Display'] mb-2">
            Find Recipes
          </h1>
          <p className="text-muted-foreground">
            Search thousands of delicious recipes to add to your meal plan
          </p>
        </div>

        {/* Search Form */}
        <Card className="border-border/50 mb-8">
          <CardContent className="pt-6">
            <form onSubmit={handleSearch}>
              <div className="flex gap-2 mb-4">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
                  <Input
                    type="text"
                    placeholder="Search recipes (e.g., 'chicken pasta', 'healthy salad')"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    className="pl-10 h-12"
                    data-testid="recipe-search-input"
                  />
                </div>
                <Button 
                  type="button" 
                  variant="outline" 
                  className="h-12"
                  onClick={() => setShowFilters(!showFilters)}
                  data-testid="toggle-filters"
                >
                  <Filter className="h-4 w-4 mr-2" />
                  Filters
                  {(diet || cuisine || maxReadyTime) && (
                    <Badge variant="secondary" className="ml-2">
                      {[diet, cuisine, maxReadyTime].filter(Boolean).length}
                    </Badge>
                  )}
                </Button>
                <Button type="submit" className="h-12 px-8" disabled={loading} data-testid="search-btn">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Search'}
                </Button>
              </div>

              {/* Filters */}
              {showFilters && (
                <div className="flex flex-wrap gap-4 pt-4 border-t border-border">
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm text-muted-foreground mb-2 block">Diet</label>
                    <Select value={diet || "all"} onValueChange={(val) => setDiet(val === "all" ? "" : val)}>
                      <SelectTrigger data-testid="diet-filter">
                        <SelectValue placeholder="Any diet" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any diet</SelectItem>
                        {diets.map(d => (
                          <SelectItem key={d} value={d} className="capitalize">
                            {d}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm text-muted-foreground mb-2 block">Cuisine</label>
                    <Select value={cuisine || "all"} onValueChange={(val) => setCuisine(val === "all" ? "" : val)}>
                      <SelectTrigger data-testid="cuisine-filter">
                        <SelectValue placeholder="Any cuisine" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">Any cuisine</SelectItem>
                        {cuisines.map(c => (
                          <SelectItem key={c} value={c} className="capitalize">
                            {c}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex-1 min-w-[200px]">
                    <label className="text-sm text-muted-foreground mb-2 block">Cook Time</label>
                    <Select value={maxReadyTime?.toString() || "any"} onValueChange={(val) => setMaxReadyTime(val === "any" ? null : parseInt(val))}>
                      <SelectTrigger data-testid="cooktime-filter">
                        <SelectValue placeholder="Any time" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="any">Any time</SelectItem>
                        {cookTimeOptions.map(opt => (
                          <SelectItem key={opt.value} value={opt.value.toString()}>
                            {opt.emoji} {opt.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  {(diet || cuisine || maxReadyTime) && (
                    <div className="flex items-end">
                      <Button 
                        type="button" 
                        variant="ghost" 
                        size="sm"
                        onClick={clearFilters}
                        data-testid="clear-filters"
                      >
                        <X className="h-4 w-4 mr-1" />
                        Clear
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </form>

            {/* Suggested Search Chips */}
            {!searched && (
              <div className="mt-4 pt-4 border-t border-border/50" data-testid="suggested-searches">
                <p className="text-sm text-muted-foreground mb-3">Popular searches:</p>
                <div className="flex flex-wrap gap-2">
                  {suggestedSearches.map((item) => (
                    <Button
                      key={item.label}
                      variant="outline"
                      size="sm"
                      className="rounded-full hover:bg-primary/10 hover:border-primary/50 transition-colors"
                      onClick={() => handleQuickSearch(item.query, item.label)}
                      data-testid={`chip-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <span className="mr-1.5">{item.emoji}</span>
                      {item.label}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Quick Filters - shown after search */}
        {searched && (
          <div className="mb-6 space-y-3" data-testid="quick-filters-section">
            {/* Time Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground w-16">Time:</span>
              {cookTimeOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant={maxReadyTime === opt.value ? "default" : "outline"}
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    const newTime = maxReadyTime === opt.value ? null : opt.value;
                    setMaxReadyTime(newTime);
                    setCurrentPage(1);
                    if (lastSearchQuery) {
                      performSearch(lastSearchQuery, 1, { maxReadyTime: newTime });
                    }
                  }}
                  data-testid={`quick-filter-time-${opt.value}`}
                >
                  <Clock className="h-3 w-3 mr-1" />
                  {opt.label}
                </Button>
              ))}
            </div>

            {/* Diet Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground w-16">Diet:</span>
              {quickDietOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant={diet === opt.value ? "default" : "outline"}
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    const newDiet = diet === opt.value ? '' : opt.value;
                    setDiet(newDiet);
                    setCurrentPage(1);
                    if (lastSearchQuery) {
                      performSearch(lastSearchQuery, 1, { diet: newDiet });
                    }
                  }}
                  data-testid={`quick-filter-diet-${opt.value}`}
                >
                  <span className="mr-1">{opt.icon}</span>
                  {opt.label}
                </Button>
              ))}
            </div>

            {/* Cuisine Filters */}
            <div className="flex flex-wrap items-center gap-2">
              <span className="text-sm text-muted-foreground w-16">Cuisine:</span>
              {quickCuisineOptions.map((opt) => (
                <Button
                  key={opt.value}
                  variant={cuisine === opt.value ? "default" : "outline"}
                  size="sm"
                  className="h-8"
                  onClick={() => {
                    const newCuisine = cuisine === opt.value ? '' : opt.value;
                    setCuisine(newCuisine);
                    setCurrentPage(1);
                    if (lastSearchQuery) {
                      performSearch(lastSearchQuery, 1, { cuisine: newCuisine });
                    }
                  }}
                  data-testid={`quick-filter-cuisine-${opt.value}`}
                >
                  <span className="mr-1">{opt.icon}</span>
                  {opt.label}
                </Button>
              ))}
            </div>

            {/* Clear All Filters */}
            {(maxReadyTime || diet || cuisine) && (
              <div className="flex items-center pt-2 border-t border-border">
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-8 text-muted-foreground"
                  onClick={() => {
                    setMaxReadyTime(null);
                    setDiet('');
                    setCuisine('');
                    setCurrentPage(1);
                    if (lastSearchQuery) {
                      performSearch(lastSearchQuery, 1, { maxReadyTime: null, diet: '', cuisine: '' });
                    }
                  }}
                  data-testid="clear-all-filters"
                >
                  <X className="h-3 w-3 mr-1" />
                  Clear all filters
                </Button>
                <span className="text-xs text-muted-foreground ml-2">
                  {[maxReadyTime && 'Time', diet && 'Diet', cuisine && 'Cuisine'].filter(Boolean).join(' + ')} active
                </span>
              </div>
            )}
          </div>
        )}

        {/* Results */}
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {[1, 2, 3, 4, 5, 6, 7, 8].map((i) => (
              <Card key={i} className="border-border/50 overflow-hidden">
                <div className="aspect-[4/3] skeleton-pulse" />
                <CardContent className="pt-4">
                  <div className="h-5 skeleton-pulse rounded mb-2" />
                  <div className="h-4 skeleton-pulse rounded w-2/3" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : recipes.length > 0 ? (
          <>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {recipes.map((recipe) => {
              const isSelected = selectedRecipe === recipe.id;
              return (
              <Card 
                key={recipe.id} 
                className={`border-border/50 overflow-hidden recipe-card group transition-all duration-300 ${
                  isSelected 
                    ? 'ring-2 ring-primary border-primary shadow-lg scale-[1.02]' 
                    : 'hover:shadow-md'
                }`}
                data-testid={`recipe-card-${recipe.id}`}
              >
                <Link to={`/recipes/${recipe.id}`} onClick={(e) => handleRecipeClick(e, recipe)}>
                  <div className="aspect-[4/3] overflow-hidden relative">
                    <img
                      src={recipe.image}
                      alt={recipe.title}
                      className={`w-full h-full object-cover transition-transform duration-300 ${
                        isSelected ? 'scale-105' : 'group-hover:scale-105'
                      }`}
                    />
                    <div className={`absolute inset-0 bg-gradient-to-t from-black/50 to-transparent transition-opacity ${
                      isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'
                    }`} />
                    
                    {/* Selected checkmark badge */}
                    {isSelected && (
                      <div className="absolute top-3 right-3 h-8 w-8 rounded-full bg-primary flex items-center justify-center shadow-lg animate-in zoom-in-50 duration-200">
                        <Check className="h-5 w-5 text-primary-foreground" />
                      </div>
                    )}
                    
                    {/* Selected label */}
                    {isSelected && (
                      <div className="absolute bottom-3 left-3 right-3">
                        <Badge className="bg-primary/90 text-primary-foreground shadow-lg">
                          Selected for meal plan
                        </Badge>
                      </div>
                    )}
                  </div>
                </Link>
                <CardContent className="pt-4">
                  <div className="flex justify-between items-start gap-2 mb-2">
                    <Link to={`/recipes/${recipe.id}`} onClick={(e) => handleRecipeClick(e, recipe)} className="flex-1 min-w-0">
                      <h3 className={`font-medium line-clamp-2 transition-colors ${
                        isSelected ? 'text-primary' : 'group-hover:text-primary'
                      }`}>
                        {recipe.title}
                      </h3>
                    </Link>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-8 w-8"
                      onClick={() => toggleFavorite(recipe)}
                      data-testid={`favorite-btn-${recipe.id}`}
                    >
                      <Heart 
                        className={`h-4 w-4 ${favorites.has(recipe.id) ? 'fill-destructive text-destructive' : ''}`} 
                      />
                    </Button>
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground mb-2">
                    {recipe.readyInMinutes && (
                      <span className="flex items-center gap-1">
                        <Clock className="h-3.5 w-3.5" />
                        {recipe.readyInMinutes} min
                      </span>
                    )}
                    {recipe.servings && (
                      <span className="flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        {recipe.servings}
                      </span>
                    )}
                  </div>
                  {/* Health Labels */}
                  <HealthLabelBadges 
                    healthLabels={recipe.healthLabels || []}
                    dietLabels={recipe.dietLabels || []}
                    maxDisplay={3}
                    size="xs"
                    showTooltip={false}
                  />
                </CardContent>
              </Card>
            )})}
          </div>
          
          {/* Pagination Controls */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-10 mb-6" data-testid="pagination">
              {/* Previous Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage - 1)}
                disabled={currentPage === 1 || loading}
                className="gap-1"
                data-testid="pagination-prev"
              >
                <ChevronLeft className="h-4 w-4" />
                Previous
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {/* First page */}
                {currentPage > 2 && (
                  <>
                    <Button
                      variant={currentPage === 1 ? "default" : "ghost"}
                      size="sm"
                      onClick={() => goToPage(1)}
                      disabled={loading}
                      className="w-9 h-9"
                    >
                      1
                    </Button>
                    {currentPage > 3 && (
                      <span className="px-2 text-muted-foreground">...</span>
                    )}
                  </>
                )}
                
                {/* Pages around current */}
                {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
                  let pageNum;
                  if (totalPages <= 5) {
                    pageNum = i + 1;
                  } else if (currentPage <= 3) {
                    pageNum = i + 1;
                  } else if (currentPage >= totalPages - 2) {
                    pageNum = totalPages - 4 + i;
                  } else {
                    pageNum = currentPage - 2 + i;
                  }
                  
                  if (pageNum < 1 || pageNum > totalPages) return null;
                  if (pageNum === 1 && currentPage > 2) return null;
                  if (pageNum === totalPages && currentPage < totalPages - 1) return null;
                  
                  return (
                    <Button
                      key={pageNum}
                      variant={currentPage === pageNum ? "default" : "ghost"}
                      size="sm"
                      onClick={() => goToPage(pageNum)}
                      disabled={loading}
                      className="w-9 h-9"
                      data-testid={`page-${pageNum}`}
                    >
                      {pageNum}
                    </Button>
                  );
                })}
                
                {/* Last page */}
                {currentPage < totalPages - 1 && totalPages > 5 && (
                  <>
                    {currentPage < totalPages - 2 && (
                      <span className="px-2 text-muted-foreground">...</span>
                    )}
                    <Button
                      variant={currentPage === totalPages ? "default" : "ghost"}
                      size="sm"
                      onClick={() => goToPage(totalPages)}
                      disabled={loading}
                      className="w-9 h-9"
                    >
                      {totalPages}
                    </Button>
                  </>
                )}
              </div>
              
              {/* Next Button */}
              <Button
                variant="outline"
                size="sm"
                onClick={() => goToPage(currentPage + 1)}
                disabled={currentPage === totalPages || loading}
                className="gap-1"
                data-testid="pagination-next"
              >
                Next
                <ChevronRight className="h-4 w-4" />
              </Button>
            </div>
          )}
          
          {/* Results count */}
          {totalResults > 0 && (
            <p className="text-center text-sm text-muted-foreground mb-6">
              Showing {((currentPage - 1) * RESULTS_PER_PAGE) + 1}-{Math.min(currentPage * RESULTS_PER_PAGE, totalResults)} of {totalResults} recipes
            </p>
          )}
        </>
        ) : searched ? (
          <div className="text-center py-16">
            <Search className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium mb-2">No recipes found</h3>
            <p className="text-muted-foreground mb-4">
              Try adjusting your search terms or filters
            </p>
            <Button variant="outline" onClick={clearFilters} data-testid="try-different-search">
              Clear Filters
            </Button>
          </div>
        ) : (
          <div className="text-center py-16" data-testid="empty-search-state">
            <Search className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-medium mb-2">Start your search</h3>
            <p className="text-muted-foreground">
              Try &quot;chicken pasta,&quot; &quot;weeknight dinners,&quot; or &quot;vegetarian.&quot;
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RecipeSearch;
